import { t } from "../../../lib/sprache";
import { readZipText, unescapeXml, zipEntries as zipEntriesRaw, type ZipFile, type ZipMeldungen } from "../../../lib/unzip";
/**
 * Excel-Dateien (.xlsx) direkt lesen, ohne Bibliothek (Befund 13): eine .xlsx ist ein ZIP mit XML. Gelesen wird das erste
 * Tabellenblatt als Zeilen mit Zelltext (geteilte Texte, eingebettete Texte, Zahlen, Wahrheitswerte). Formeln liefern ihren
 * gespeicherten Wert. Entpacken über DecompressionStream (Browser, Node 18+).
 */

const MELDUNGEN = (): ZipMeldungen => ({
  invalid: t("Die Datei ist keine gültige Excel-Datei (.xlsx).", "The file is not a valid Excel file (.xlsx)."),
  damaged: t("Die Excel-Datei ist beschädigt.", "The Excel file is damaged."),
  unknownMethod: t("Diese Excel-Datei ist mit einem unbekannten Verfahren gepackt. Bitte als CSV speichern.", "This Excel file is packed with an unknown method. Please save it as CSV."),
});
const zipEntries = (b: Uint8Array): ZipFile[] => zipEntriesRaw(b, MELDUNGEN());
const readEntry = (b: Uint8Array, f: ZipFile): Promise<string> => readZipText(b, f, MELDUNGEN());

/** Text eines <si>- oder <is>-Elements: alle <t>-Teile (auch aus formatierten Teilstücken) hintereinander. */
function textOf(xml: string): string {
  return [...xml.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)].map((m) => unescapeXml(m[1]!)).join("");
}
/** „AB12“ → Spaltenindex 27 (0-basiert) */
export function columnIndex(ref: string): number {
  const letters = /^[A-Z]+/i.exec(ref)?.[0].toUpperCase() ?? "A";
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

/** Erstes Tabellenblatt einer .xlsx als Zeilen; leere Zeilen fallen weg. */
export async function readXlsx(buffer: ArrayBuffer | Uint8Array): Promise<string[][]> {
  const b = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const files = zipEntries(b);
  const byName = new Map(files.map((f) => [f.name.replace(/^\//, ""), f]));
  // Erstes Blatt laut Arbeitsmappe (Reihenfolge der Reiter), sonst das erste worksheets/*.xml
  let sheetPath: string | null = null;
  const wb = byName.get("xl/workbook.xml"), rels = byName.get("xl/_rels/workbook.xml.rels");
  if (wb && rels) {
    const firstId = /<sheet\b[^>]*\br:id="([^"]+)"/.exec(await readEntry(b, wb))?.[1];
    const relXml = await readEntry(b, rels);
    const target = firstId ? new RegExp(`<Relationship\\b[^>]*Id="${firstId}"[^>]*Target="([^"]+)"`).exec(relXml)?.[1]
      ?? new RegExp(`<Relationship\\b[^>]*Target="([^"]+)"[^>]*Id="${firstId}"`).exec(relXml)?.[1] : undefined;
    if (target) sheetPath = target.startsWith("/") ? target.slice(1) : `xl/${target.replace(/^\.\//, "")}`;
  }
  const sheet = (sheetPath && byName.get(sheetPath)) || files.filter((f) => /^xl\/worksheets\/[^/]+\.xml$/.test(f.name)).sort((x, y) => x.name.localeCompare(y.name))[0];
  if (!sheet) throw new Error(t("In der Excel-Datei gibt es kein Tabellenblatt.", "The Excel file has no worksheet."));
  const sharedFile = byName.get("xl/sharedStrings.xml");
  const shared = sharedFile ? [...(await readEntry(b, sharedFile)).matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => textOf(m[1]!)) : [];
  const xml = await readEntry(b, sheet);
  const rows: string[][] = [];
  for (const rm of xml.matchAll(/<row\b[^>]*?(?:\/>|>([\s\S]*?)<\/row>)/g)) {
    const row: string[] = [];
    let next = 0;
    for (const cm of (rm[1] ?? "").matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = cm[1] ?? "";
      const ref = /\br="([A-Z]+\d+)"/.exec(attrs)?.[1];
      const idx = ref ? columnIndex(ref) : next;
      const type = /\bt="([^"]+)"/.exec(attrs)?.[1] ?? "n";
      const inner = cm[2] ?? "";
      const v = /<v>([\s\S]*?)<\/v>/.exec(inner)?.[1];
      let value = "";
      if (type === "s") value = shared[Number(v)] ?? "";
      else if (type === "inlineStr") value = textOf(inner);
      else if (type === "b") value = v === "1" ? "ja" : "nein";
      else if (v !== undefined) value = unescapeXml(v);
      while (row.length < idx) row.push("");
      row[idx] = value;
      next = idx + 1;
    }
    if (row.some((c) => c.trim() !== "")) rows.push(row);
  }
  return rows;
}

/** Ist das eine .xlsx (ZIP-Kopf „PK“)? */
export function looksLikeXlsx(name: string, head: Uint8Array): boolean {
  return /\.xlsx$/i.test(name) || (head[0] === 0x50 && head[1] === 0x4b && head[2] === 0x03 && head[3] === 0x04);
}
